import type { ApiResponse } from '@/lib/api-contract';
import { isStaleWriteCode, type StaleWriteActor } from '@shared/types/stale-write';

/**
 * A save refused because someone else saved the record first (409 `<DOMAIN>_STALE`, decision
 * 44 a): who and when, for DataChangedBanner. The page keeps the person's draft and offers Reload.
 */
export interface StaleChange {
  /** Their name, or null when unknown (a sync, or someone who has left the team). */
  changedBy: string | null;
  /** When they saved (ISO), when the server said. */
  changedAt: string | null;
}

/** The stale change in a failed save's answer, or null when it failed for another reason. */
export function staleChangeOf(res: Pick<ApiResponse<unknown>, 'status' | 'code' | 'body'>): StaleChange | null {
  if (res.status !== 409 || !isStaleWriteCode(res.code)) return null;
  const by = res.body?.updated_by as StaleWriteActor | null | undefined;
  const at = res.body?.updated_at;
  return {
    changedBy: typeof by?.name === 'string' && by.name.trim() ? by.name.trim() : null,
    changedAt: typeof at === 'string' ? at : null,
  };
}

/** The newer of two versions (ISO times); either may be missing. */
export function newerVersion(a: string | null | undefined, b: string | null | undefined): string | null {
  if (!a) return b ?? null;
  if (!b) return a;
  return Date.parse(b) > Date.parse(a) ? b : a;
}
